/**
 * Session start tracker for Mixpanel (fhs-session-started).
 * Uses sessionStorage to detect the first FHS page view in a user session.
 * Fires once per tab/window; session resets when tab/window closes.
 *
 * Expects window.fhsAnalytics and window.fhsAnalytics.context from analytics.js.
 */

import { getDeviceType } from './get-device-type.js';

const SESSION_EVENT = 'fhs-session-started';
const SESSION_STORAGE_KEY = 'fhs-session-started';

/**
 * Derive traffic source from UTM params or document.referrer.
 * @param {{ utm_source?: string }} ctx
 * @returns {string}
 */
function getTrafficSource(ctx) {
  if (ctx?.utm_source) return ctx.utm_source;
  if (typeof document === 'undefined' || !document.referrer) return 'direct';
  try {
    const ref = new URL(document.referrer);
    return ref.origin === window.location.origin
      ? 'internal'
      : ref.hostname || 'referral';
  } catch {
    return 'referral';
  }
}

/**
 * Session-specific properties only. track() merges context (page_type, fh_ct, utm_*, etc.).
 * Omit entry_theme (Theme super prop), entry_page_type/page_type, entry_page_url/$current_url, client_tier/fh_ct.
 */
function getSessionProperties() {
  const ctx = window.fhsAnalytics?.context || {};
  return {
    device_type: getDeviceType(),
    traffic_source: getTrafficSource(ctx),
  };
}

/**
 * Check if this is a new session and fire fhs-session-started if so.
 * Uses sessionStorage: resets when tab/window closes.
 */
function tryFireSessionStarted() {
  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
    return;
  }
  if (!window.fhsAnalytics?.track) {
    return;
  }
  if (window.fhsAnalytics.shouldTrack === false) {
    return;
  }

  try {
    if (sessionStorage.getItem(SESSION_STORAGE_KEY)) {
      return; // Already fired this session
    }

    const props = getSessionProperties();
    sessionStorage.setItem(SESSION_STORAGE_KEY, '1');
    window.fhsAnalytics.track(SESSION_EVENT, props);
  } catch (e) {
    // sessionStorage may throw in private mode or when storage is full
    if (typeof console !== 'undefined' && console.warn) {
      console.warn('[fhs-session-tracker]', e);
    }
  }
}

/**
 * Initialize session tracking. Fires on first page load only.
 */
function init() {
  if (typeof document === 'undefined') return;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', tryFireSessionStarted);
  } else {
    tryFireSessionStarted();
  }
}

init();
